import { useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Stack, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { sortByPropertyNumber, propertyMatches } from "./bkaUtils";

export default function BulkCreateDialog({ properties, periods, onCreated }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(String(new Date().getFullYear() - 1));
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [copyPrev, setCopyPrev] = useState(true);
  const [busy, setBusy] = useState(false);

  const existing = useMemo(() => new Set(periods.filter((p) => p.status !== "cancelled" && (p.period_from || "").startsWith(year)).map((p) => p.property_id)), [periods, year]);
  const list = useMemo(() => sortByPropertyNumber(properties).filter((p) => propertyMatches(p, q)), [properties, q]);
  const selectable = list.filter((p) => !existing.has(p.id));
  const chosen = Object.keys(sel).filter((k) => sel[k] && !existing.has(k));
  const allOn = selectable.length > 0 && selectable.every((p) => sel[p.id]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bka/periods/bulk", { property_ids: chosen, year: parseInt(year, 10), copy_previous: copyPrev });
      toast.success(`${data.created.length} Abrechnung(en) angelegt${data.skipped.length ? ` · ${data.skipped.length} übersprungen` : ""}`);
      setSel({}); setOpen(false); onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Massen-Erstellung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" className="gap-1.5" onClick={() => setOpen(true)} data-testid="bka-bulk-btn">
        <Stack size={16} /> Massen-Erstellung
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="bka-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Mehrere Abrechnungen anlegen</DialogTitle>
            <DialogDescription>Legt je Objekt eine leere Entwurfs-Abrechnung an. Objekte mit bestehender Abrechnung für das Jahr werden übersprungen.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-[120px_1fr] gap-3 items-end">
            <div><Label>Wirtschaftsjahr</Label><Input type="number" min="2000" max="2099" value={year} onChange={(e) => setYear(e.target.value)} data-testid="bka-bulk-year" /></div>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-8" placeholder="Objekt, Objekt-ID oder Adresse suchen…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bka-bulk-search" />
            </div>
          </div>
          <div className="border border-slate-200 rounded max-h-[45vh] overflow-y-auto">
            <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-medium sticky top-0 cursor-pointer">
              <Checkbox checked={allOn} onCheckedChange={(v) => setSel((c) => { const n = { ...c }; selectable.forEach((p) => { n[p.id] = !!v; }); return n; })} data-testid="bka-bulk-select-all" />
              Alle sichtbaren auswählen ({selectable.length})
            </label>
            {list.map((p) => {
              const has = existing.has(p.id);
              return (
                <label key={p.id} className={`flex items-center gap-2 px-3 py-1.5 text-sm border-b border-slate-100 last:border-b-0 ${has ? "opacity-50" : "cursor-pointer hover:bg-slate-50"}`} data-testid={`bka-bulk-row-${p.id}`}>
                  <Checkbox disabled={has} checked={!has && !!sel[p.id]} onCheckedChange={(v) => setSel((c) => ({ ...c, [p.id]: !!v }))} data-testid={`bka-bulk-check-${p.id}`} />
                  <span className="font-mono text-xs text-slate-500 w-20 shrink-0">{p.property_number || "—"}</span>
                  <span className="flex-1 truncate">{p.name}</span>
                  {has && <span className="text-[10px] text-slate-500">bereits vorhanden</span>}
                </label>
              );
            })}
            {list.length === 0 && <div className="text-xs text-slate-500 p-4 text-center">Keine Objekte gefunden.</div>}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={copyPrev} onCheckedChange={(v) => setCopyPrev(!!v)} data-testid="bka-bulk-copy-prev" />
            Positionen aus dem Vorjahr übernehmen (Beträge auf 0)
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={submit} disabled={busy || chosen.length === 0} data-testid="bka-bulk-submit">
              {busy ? "Lege an…" : `${chosen.length} Abrechnung(en) anlegen`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
